import React, { useEffect, useState } from 'react';
import { CheckCircle2, Clock } from 'lucide-react';
import { financialService } from '@services/financialService';
import type { PixReceivable } from '@/types/financial';

// Recebimento PIX do pedido: o registro criado automaticamente quando o
// PaymentHub confirma o pagamento (lançamento no Conta Corrente + taxa
// prevista + conciliação com o extrato do Itaú).
//
// O vínculo é pelo número do pedido na LOJA (order_num_ecommerce) ou pela
// proposta do CRM — são essas as chaves que o PaymentHub grava no pagamento.
// Sem registro, o bloco não aparece (pedido pago por cartão, ou PIX anterior à
// funcionalidade).

const formatBRL = (cents: number): string =>
  (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatPaidAt = (paidAt?: string): string => {
  if (!paidAt) return '—';
  const [date, time] = paidAt.split('T');
  const [y, m, d] = date.split('-');
  return `${d}/${m}/${y}${time ? ` ${time.slice(0, 5)}` : ''}`;
};

export const PixReceiptCard: React.FC<{
  orderNumEcommerce?: string;
  proposalNum?: number;
}> = ({ orderNumEcommerce, proposalNum }) => {
  const [receivables, setReceivables] = useState<PixReceivable[]>([]);

  useEffect(() => {
    let cancelled = false;
    const orderId = orderNumEcommerce ? Number(orderNumEcommerce) : undefined;
    if ((orderId == null || Number.isNaN(orderId)) && proposalNum == null) return;
    (async () => {
      try {
        const list = await financialService.getPixReceivables(
          orderId != null && !Number.isNaN(orderId) ? { orderId } : { proposalId: proposalNum },
        );
        if (!cancelled) setReceivables(list);
      } catch {
        // Bloco informativo: sem permissão ou erro de rede, simplesmente não
        // aparece — o pedido continua utilizável.
        if (!cancelled) setReceivables([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orderNumEcommerce, proposalNum]);

  if (receivables.length === 0) return null;

  // Mesmo markup do <Field> do detalhe do pedido — o bloco inteiro (rótulo
  // incluso) só existe quando há recebimento PIX registrado.
  return (
    <div>
      <dt className="text-xs text-slate-500 uppercase tracking-wider">Recebimento PIX</dt>
      <dd className="mt-0.5 space-y-1.5">
      {receivables.map((r) => (
        <div key={r.pixReceivableId} className="text-sm text-slate-900">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium">{formatBRL(r.grossAmountCents)}</span>
            <span className="text-slate-500">em {formatPaidAt(r.paidAt)}</span>
            {r.status === 1 ? (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                <CheckCircle2 size={12} /> Confirmado pelo banco
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-slate-50 text-slate-500 border border-slate-200">
                <Clock size={12} /> Aguardando extrato
              </span>
            )}
          </div>
          <p
            className="text-xs text-slate-500"
            title="Taxa estimada pelo cadastro de taxas PIX. O Itaú credita cada PIX cheio e cobra a tarifa consolidada uma vez por mês."
          >
            Taxa prevista: {formatBRL(r.feeAmountCents)}
            {r.feePercent > 0 && ` (${r.feePercent.toLocaleString('pt-BR')}%)`}
            {r.financialTransactionId ? ` · Lançamento #${r.financialTransactionId}` : ' · Lançamento pendente'}
          </p>
        </div>
      ))}
      </dd>
    </div>
  );
};
